import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { properties } from "../data/properties";
import { getDescription }    from "../i18n/getDescription";
import { getAmenityLabel }   from "../i18n/getAmenityLabel";
import Lightbox from "../components/shared/Lightbox";
import {
  PHOTO_GRID_COLS,
  PHOTOS_PREVIEW_COUNT,
  SIMILAR_PROPERTIES_MAX,
  WA_URL, TG_URL, PHONE_RAW, PHONE_DISPLAY, SITE_URL,
} from "../config/constants";
import "./PropertyDetail.css";

const FALLBACKS = {
  Villa:      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=85",
  Studio:     "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=85",
  Commercial: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=85",
  Apartment:  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=85",
};

export default function PropertyDetail() {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const { t, i18n } = useTranslation("property");
  const { t: tc } = useTranslation("common");

  const property = properties.find(p => p.slug === slug);

  // Both useMemo calls must precede the early return to satisfy Rules of Hooks.
  // When property is null they return safe empty defaults.
  const description = useMemo(
    () => property ? getDescription(property, i18n.language) : null,
    [property, i18n.language]
  );
  const translatedAmenities = useMemo(
    () => (property?.amenities ?? []).map(a => getAmenityLabel(a, i18n.language)),
    [property?.amenities, i18n.language]
  );

  if (!property) return (
    <main className="pd-notfound container">
      <h1>{t("notFound")}</h1>
      <Link to="/listings">{t("backToListings")}</Link>
    </main>
  );

  // `amenities` removed — consumed via `translatedAmenities`
  const { title, location, price, priceType, beds, baths, sqft, type,
          developer, images, pfUrl, reference } = property;

  const fallback = FALLBACKS[type] || FALLBACKS["Apartment"];
  const photos = images && images.length > 0 ? images.filter(Boolean) : [fallback];

  const similar = properties
    .filter(p => p.id !== property.id && p.emirate === property.emirate)
    .slice(0, SIMILAR_PROPERTIES_MAX);

  const pageUrl = `https://pronet-realestate.com/property/${slug}`;
  const metaDesc = property.description
    ? property.description.replace(/\n+/g, " ").trim().slice(0, 160)
    : `${type} ${priceType === "rent" ? "for rent" : "for sale"} in ${location}.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "name": title,
    "description": metaDesc,
    "url": pageUrl,
    "image": photos[0],
    "numberOfRooms": beds,
    "floorSize": { "@type": "QuantitativeValue", "value": sqft, "unitCode": "FTK" },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "AED",
      "availability": "https://schema.org/InStock",
      "priceSpecification": priceType === "rent"
        ? { "@type": "UnitPriceSpecification", "price": price, "priceCurrency": "AED", "referenceQuantity": { "@type": "QuantitativeValue", "value": 1, "unitCode": "ANN" } }
        : undefined
    },
    "address": { "@type": "PostalAddress", "streetAddress": location, "addressCountry": "AE" },
    "broker": { "@type": "RealEstateAgent", "name": "ProNet Real Estate", "url": SITE_URL, "telephone": PHONE_RAW }
  };

  const badge = tc(priceType === "rent" ? "badge.rent" : "badge.sale");

  return (
    <main className="pd">
      <Helmet>
        <title>{title} — {location} | ProNet Real Estate</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={`${title} — ProNet Real Estate`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={photos[0]} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {lightbox !== null && (
        <Lightbox
          photos={photos}
          initialIndex={lightbox}
          showWatermark={!reference}
          onClose={() => setLightbox(null)}
        />
      )}

      <div className="pd-hero">
        <span className={`pd-badge ${priceType === "rent" ? "badge-rent" : "badge-sale"}`}>
          {badge}
        </span>
        <h1 className="pd-title">{title}</h1>
        <p className="pd-loc">{location}</p>
      </div>

      <div className="pd-photos" style={{ gridTemplateColumns: photos.length >= 2 ? PHOTO_GRID_COLS : "1fr" }}>
        <div className="pd-photo pd-photo-main" role="button" tabIndex={0} onClick={() => setLightbox(0)} onKeyDown={(e) => e.key === "Enter" && setLightbox(0)}>
          <img src={photos[0]} alt={title} loading="eager"
            onError={e => { e.target.src = fallback; }} />
          {photos.length > 1 && (
            <div className="pd-photo-hint">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="2" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <rect x="11" y="2" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <rect x="2" y="11" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <rect x="11" y="11" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
              </svg>
              {t("viewAll", { count: photos.length })}
            </div>
          )}
        </div>
        {photos.length >= 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {photos.slice(1, 1 + PHOTOS_PREVIEW_COUNT).map((src, i) => (
              <div key={i} className="pd-photo pd-photo-sm" role="button" tabIndex={0} onClick={() => setLightbox(i + 1)} onKeyDown={(e) => e.key === "Enter" && setLightbox(i + 1)}>
                <img src={src} alt={`${title} ${i + 2}`} loading="lazy"
                  onError={e => { e.target.src = fallback; }} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="container pd-layout">
        <div className="pd-main">
          <div className="pd-breadcrumb">
            <Link to="/listings">{tc("nav.listings")}</Link> / {title}
          </div>

          <div className="pd-stats-row">
            <div className="pd-stat">
              <span className="pd-stat-n">{beds > 0 ? beds : "—"}</span>
              <span className="pd-stat-l">{t("bedrooms")}</span>
            </div>
            <div className="pd-stat">
              <span className="pd-stat-n">{baths}</span>
              <span className="pd-stat-l">{t("bathrooms")}</span>
            </div>
            <div className="pd-stat">
              <span className="pd-stat-n">{sqft > 0 ? sqft.toLocaleString() : "—"}</span>
              <span className="pd-stat-l">{t("sqft")}</span>
            </div>
            <div className="pd-stat">
              <span className="pd-stat-n">{type}</span>
              <span className="pd-stat-l">{t("type")}</span>
            </div>
          </div>

          {description && (
            <div className="pd-sec">
              <h2 className="pd-sec-title">{t("description")}</h2>
              <div className="pd-desc">
                {description.split(/\n+/).map(s => s.trim()).filter(Boolean).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>
          )}

          {translatedAmenities.length > 0 && (
            <div className="pd-sec">
              <h2 className="pd-sec-title">{t("amenities")}</h2>
              <div className="pd-amenities">
                {translatedAmenities.map((a) => <span key={a} className="pd-amenity">{a}</span>)}
              </div>
            </div>
          )}

          <div className="pd-sec">
            <h2 className="pd-sec-title">{t("details")}</h2>
            <table className="pd-table"><tbody>
              {developer && <tr><td>{t("developer")}</td><td>{developer}</td></tr>}
              <tr><td>{t("type")}</td><td>{type}</td></tr>
              <tr><td>{t("status")}</td><td>{badge}</td></tr>
              <tr><td>{t("location")}</td><td>{location}</td></tr>
              {sqft > 0 && <tr><td>{t("size")}</td><td>{sqft.toLocaleString()} sq ft</td></tr>}
              {pfUrl && <tr><td>{t("source")}</td><td><a href={pfUrl} target="_blank" rel="noreferrer" style={{color:"var(--gold)"}}>PropertyFinder ↗</a></td></tr>}
            </tbody></table>
          </div>
        </div>

        <aside className="pd-sidebar">
          <div className="pd-price-card">
            <div className="pd-price">{new Intl.NumberFormat("en-AE").format(price)} AED</div>
            {priceType === "rent" && <p className="pd-price-note">{t("perYear")}</p>}
            <div className="pd-price-rule" />
            <a href={WA_URL} target="_blank" rel="noreferrer" className="pd-btn pd-btn-wa">{t("whatsapp")}</a>
            <a href={TG_URL} target="_blank" rel="noreferrer" className="pd-btn pd-btn-tg">{t("telegram")}</a>
            <a href={`tel:${PHONE_RAW}`} className="pd-btn pd-btn-ph">{PHONE_DISPLAY}</a>
          </div>
        </aside>
      </div>

      {similar.length > 0 && (
        <section className="pd-similar">
          <div className="container">
            <div className="section-hd">
              <div>
                <p className="section-eyebrow">{t("youMayLike")}</p>
                <h2 className="section-title">{t("similar")} <em>{t("similarEm")}</em></h2>
              </div>
            </div>
            <div className="pd-similar-grid">
              {similar.map(p => {
                const pPhoto = (p.images && p.images[0]) || FALLBACKS[p.type] || FALLBACKS["Apartment"];
                return (
                  <Link key={p.id} to={`/property/${p.slug}`} className="pd-sim-card">
                    <div className="pd-sim-img">
                      <img src={pPhoto} alt={p.title} loading="lazy"
                        onError={e => { e.target.src = FALLBACKS[p.type] || FALLBACKS["Apartment"]; }} />
                    </div>
                    <div className="pd-sim-body">
                      <div className="pd-sim-price">{new Intl.NumberFormat("en-AE").format(p.price)} AED</div>
                      <div className="pd-sim-title">{p.title}</div>
                      <div className="pd-sim-loc">{p.location}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
